<template>
  <div class="alert-panel">
    <div v-for="alert in alerts" :key="alert.id">
      <b-alert
        class="alert-panel__alert-item"
        :variant="alert.type"
        dismissible
        fade
        :show="shouldShow(alert)"
        @dismissed="dismissAlert(alert)"
      >
        <div
          class="alert-panel__content-wrapper"
        >
          <strong
            class="alert-panel__alert-title"
            v-if="alert.$translatedTitle"
            v-html="alert.$translatedTitle"
          />
          <span
            class="alert-panel__alert-content"
            v-if="alert.$translatedMessage"
            v-html="alert.$translatedMessage">
          </span>
          <ul
            v-if="alert.messageList && alert.messageList.length > 0"
            >
            <li v-for="(subMessage, idx) in alert.messageList"
              :key="idx"
              v-html="subMessage.$translatedMessage"></li>
          </ul>
        </div>
      </b-alert>
    </div>
  </div>
</template>

<script>
import { onNewAlert, alertModes } from '@/services/messages/alertService';

const ALERT_AUTO_DISMISS_DELAY = 10000;
const FADE_ANIMATION_DELAY = 5000;

export default {
  components: {
  },
  props: {},
  data() {
    return {
      notifications: [],
      notificationsToAutoDismiss: {},
      listenerCancel: null,
    };
  },
  computed: {
    alerts() {
      return this.notifications.filter(notification => notification.mode === alertModes.ALERT);
    },
  },
  created() {
    this.listenerCancel = onNewAlert(notification => this.newAlert(notification));
  },
  beforeDestroy() {
    this.listenerCancel();
  },
  methods: {
    newAlert(notification) {
      this.translateAlert(notification);
      this.notifications.push(notification);
      if (notification.mode === alertModes.TOAST) {
        this.getToastAction(notification)(notification.$translatedMessage,
          this.getToastConfig(notification));
      }

      if (notification.autoDismissible) {
        this.setAutoDismiss(notification);
      }
    },
    setAutoDismiss(notification) {
      this.$set(this.notificationsToAutoDismiss, notification.id, {
        timeout: setTimeout(() => {
          this.$set(this.notificationsToAutoDismiss[notification.id], 'dismissed', true);
        }, ALERT_AUTO_DISMISS_DELAY),
        dismissed: false,
      });
    },
    tryStopAutoDismiss(notification) {
      if (!this.notificationsToAutoDismiss[notification.id]
        || this.notificationsToAutoDismiss[notification.id].timeout == null) {
        return;
      }

      clearTimeout(this.notificationsToAutoDismiss[notification.id].timeout);
      this.$set(this.notificationsToAutoDismiss[notification.id], 'timeout', null);
    },
    tryClearAutoDismiss(notification) {
      if (!this.notificationsToAutoDismiss[notification.id]) {
        return;
      }

      this.$set(this.notificationsToAutoDismiss, notification.id, null);
      delete this.notificationsToAutoDismiss[notification.id];
    },
    dismissAlert(notification) {
      this.tryStopAutoDismiss(notification);
      notification.dismiss();
      this.removeAfterFadeAnimation(notification);
    },
    removeAfterFadeAnimation(notification) {
      setTimeout(() => {
        this.notifications.splice(this.notifications.indexOf(notification), 1);
        this.tryClearAutoDismiss(notification);
      }, FADE_ANIMATION_DELAY);
    },
    translateAlert(notification) {
      this.translateItem(notification);

      if (Array.isArray(notification.messageList)) {
        notification.messageList.forEach(messageItem => this.translateItem(messageItem));
      }
    },
    translateItem(messageItem) {
      const translations = {};

      if (messageItem.message) {
        translations.$translatedMessage = this.$t(messageItem.message, messageItem.messageParams);
      }

      if (messageItem.title) {
        translations.$translatedTitle = this.$t(messageItem.title, messageItem.titleParams);
      }

      Object.assign(messageItem, translations);
    },
    getToastAction(notification) {
      switch (notification.type) {
        case 'info':
          return this.$toasted.info;
        case 'success':
          return this.$toasted.success;
        case 'danger':
          return this.$toasted.error;
        default:
          return this.$toasted.show;
      }
    },
    getToastConfig(notification) {
      return {
        icon: this.getToastIcon(notification),
        theme: 'toasted-primary',
        position: 'top-right',
        duration: 2000,
      };
    },
    getToastIcon(notification) {
      switch (notification.type) {
        case 'success':
          return 'check';
        case 'danger':
          return 'times';
        default:
          return null;
      }
    },
    shouldShow(notification) {
      return !this.notificationsToAutoDismiss[notification.id]
        || !this.notificationsToAutoDismiss[notification.id].dismissed;
    },
  },
};
</script>

<style lang="sass" scoped>
@import src/styles/defaults/variables
@import "src/styles/defaults/mobile-first"
@import "src/styles/components/global-z-Index"

.alert-panel
  position: fixed
  display: flex
  flex-direction: column
  align-self: center
  z-index: $z-index-alert-panel
  &__alert-title
    display: inline-block
  ::v-deep
    & .btn-link
      color: $color-link
      &:hover
        color: darken($color-link, 15)
  > *:first-child
    margin-top: 10px
  &__content-wrapper
    max-height: 75vh
    overflow-y: auto
  +media-min-sm()
    width: 80%
    min-width: 500px
  +media-min-md()
    width: 50%
    max-width: 700px
  +media-min-lg()
</style>
